import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { CircleCheck as CheckCircle, ArrowRight, Phone, Target, Shield, Lock, TriangleAlert as AlertTriangle, FileCheck } from 'lucide-react';

const ITSicherheitISMS = () => {
  const seoJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'IT-Sicherheit & ISMS Mönchengladbach',
    description: 'IT-Sicherheit für KMU in Mönchengladbach: Grundlegende Maßnahmen ohne Panik. DSGVO, Backups, Passwörter – pragmatisch und umsetzbar.',
    provider: { '@type': 'LocalBusiness', name: 'Schteffann', url: 'https://schteffann.de/' },
    areaServed: ['Mönchengladbach', 'Viersen', 'Krefeld', 'Neuss', 'Düsseldorf', 'Nordrhein-Westfalen'],
    serviceType: 'IT-Sicherheit'
  };

  const probleme = [
    'Unsicherheit bei Datenschutz und DSGVO',
    'Angst vor Hackerangriffen, aber keine Ahnung, wo anfangen',
    'Passwörter auf Zetteln oder immer die gleichen',
    'Backups? Ja, irgendwo. Ob sie funktionieren? Ungetestet.',
    'Jeder kann auf alles zugreifen – keine Zugriffsrechte'
  ];

  const massnahmen = [
    {
      icon: Lock,
      title: 'Passwortverwaltung',
      text: 'Sichere Passwörter für alle. Mit einem Passwortmanager – einfach zu nutzen, deutlich sicherer.'
    },
    {
      icon: Shield,
      title: 'Zwei-Faktor-Authentifizierung',
      text: 'Ein zweiter Schutzschritt beim Login. Verhindert 99% aller Zugriffe durch Fremde.'
    },
    {
      icon: FileCheck,
      title: 'Backup-Strategie',
      text: 'Regelmäßige Sicherungen, die auch wirklich funktionieren. Mit Test-Wiederherstellung.'
    },
    {
      icon: AlertTriangle,
      title: 'Zugriffsrechte',
      text: 'Wer darf was sehen? Klare Regeln, damit nicht jeder auf alles zugreifen kann.'
    }
  ];

  const isms = [
    'ISMS klingt kompliziert – ist aber nur ein Managementsystem für IT-Sicherheit',
    'Es geht darum: Risiken erkennen, Maßnahmen festlegen, regelmäßig prüfen',
    'Für KMU reichen oft einfache Grundlagen: Wer darf was? Was ist kritisch? Wie sichern wir ab?',
    'Ich helfe Ihnen, ein schlankes ISMS aufzubauen – ohne unnötigen Papierkram',
    'Ergebnis: Übersicht, klare Regeln, weniger Risiko'
  ];

  const realitaet = [
    {
      mythos: 'Wir sind zu klein für Cyberangriffe',
      realitaet: 'Gerade KMU sind Ziel, weil sie oft schlechter geschützt sind'
    },
    {
      mythos: 'Sicherheit ist teuer und kompliziert',
      realitaet: 'Die Basics sind einfach und kosten wenig – bringen aber viel'
    },
    {
      mythos: 'DSGVO betrifft uns nicht wirklich',
      realitaet: 'Jedes Unternehmen mit Kundendaten muss sich dran halten'
    }
  ];

  const vorgehen = [
    'Risikoanalyse: Was ist bei Ihnen kritisch?',
    'Quick Wins: Welche Maßnahmen bringen schnell Sicherheit?',
    'Umsetzung: Passwortmanager, 2FA, Backups, Zugriffsrechte einrichten',
    'Schulung: Ihr Team lernt, worauf es ankommt',
    'Dokumentation: Einfache Sicherheitsrichtlinien',
    'Regelmäßige Checks: Was muss aktualisiert werden?'
  ];

  const relatedPages = [
    { title: 'IT-Infrastruktur', url: '/it-infrastruktur-kmu', description: 'Sichere Basis aufbauen' },
    { title: 'Microsoft 365', url: '/microsoft-365-optimierung', description: 'Sicherheitsfunktionen nutzen' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEOHead
        title="IT-Sicherheit & ISMS für KMU in Mönchengladbach – Schteffann"
        description="IT-Sicherheit für KMU in Mönchengladbach: Grundlegende Maßnahmen ohne Panik. DSGVO, Backups, Passwörter – pragmatisch und umsetzbar. Raum Mönchengladbach, Viersen, Krefeld."
        keywords="IT-Sicherheit Mönchengladbach, ISMS KMU, DSGVO Beratung, Cybersecurity KMU, IT-Sicherheitskonzept, Backup Strategie, Passwortmanagement, Mönchengladbach, Viersen, Krefeld, NRW"
        jsonLd={seoJsonLd}
      />
      {/* Hero */}
      <section className="relative py-20 lg:py-24 overflow-hidden bg-gradient-to-br from-cyan-50/50 via-blue-50/30 to-slate-50">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-blue-600/5"></div>
        <div className="container relative">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-gradient-to-r from-cyan-100 to-blue-100 text-cyan-800 border-cyan-200 px-6 py-2">
              <Shield className="w-4 h-4 mr-2" />
              IT-Sicherheit
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-slate-900">IT-Sicherheit </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-700">ohne Panik</span>
            </h1>
            <p className="text-xl lg:text-2xl text-slate-600 leading-relaxed">
              Sicherheit muss nicht kompliziert sein. Ich zeige Ihnen, welche Maßnahmen wirklich wichtig sind – realistisch und umsetzbar.
            </p>
          </div>
        </div>
      </section>

      {/* Problem */}
      <section className="py-20">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Unsicherheit bei IT-Sicherheit</h2>
            <Card className="border-slate-200 shadow-lg">
              <CardContent className="p-8">
                <div className="space-y-4">
                  {probleme.map((problem, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="h-6 w-6 text-cyan-600 mt-1 flex-shrink-0" />
                      <p className="text-lg text-slate-700">{problem}</p>
                    </div>
                  ))}
                </div>
                <p className="text-center text-lg text-slate-700 mt-8 font-medium">
                  Das Gute: Mit ein paar grundlegenden Maßnahmen sind Sie deutlich besser geschützt.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Maßnahmen */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-cyan-50/30">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Die wichtigsten Sicherheitsmaßnahmen
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Diese vier Dinge sollte jedes KMU umsetzen:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {massnahmen.map((massnahme, index) => (
              <Card key={index} className="bg-white border-slate-200 shadow-sm">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4 mb-4">
                    <div className="p-3 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl">
                      <massnahme.icon className="h-6 w-6 text-cyan-600" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 pt-2">{massnahme.title}</h3>
                  </div>
                  <p className="text-slate-600 leading-relaxed">{massnahme.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ISMS */}
      <section className="py-20">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">ISMS-Grundlagen für KMU</h2>
            <Card className="border-slate-200 shadow-lg">
              <CardContent className="p-8">
                <div className="space-y-4">
                  {isms.map((punkt, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="h-6 w-6 text-cyan-600 mt-1 flex-shrink-0" />
                      <p className="text-lg text-slate-700">{punkt}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Mythen */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-cyan-50/30">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Mythos vs. Realität
            </h2>
          </div>

          <div className="space-y-6 max-w-4xl mx-auto">
            {realitaet.map((item, index) => (
              <Card key={index} className="border-slate-200 shadow-sm">
                <CardContent className="p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-sm font-semibold text-red-600 uppercase tracking-wider mb-3">Mythos</h4>
                      <p className="text-slate-700 font-medium">{item.mythos}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-green-600 uppercase tracking-wider mb-3">Realität</h4>
                      <p className="text-slate-700 font-medium">{item.realitaet}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Vorgehen */}
      <section className="py-20">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Wie ich vorgehe</h2>
            <Card className="border-slate-200 shadow-lg">
              <CardContent className="p-8">
                <div className="space-y-4">
                  {vorgehen.map((schritt, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-full flex items-center justify-center text-cyan-700 font-bold">
                        {index + 1}
                      </div>
                      <p className="text-lg text-slate-700 pt-1">{schritt}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-cyan-50/30">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Das könnte Sie auch interessieren</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {relatedPages.map((page, index) => (
              <Link key={index} to={page.url}>
                <Card className="group hover:shadow-xl transition-all duration-300 border-slate-200 hover:border-cyan-200 h-full">
                  <CardContent className="p-8">
                    <h3 className="text-xl font-bold text-slate-900 group-hover:text-cyan-700 transition-colors mb-3">
                      {page.title}
                    </h3>
                    <p className="text-slate-600 mb-4">{page.description}</p>
                    <div className="flex items-center text-cyan-600 group-hover:text-cyan-700">
                      <span className="text-sm font-medium">Mehr erfahren</span>
                      <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="container">
          <Card className="bg-gradient-to-br from-cyan-600 via-blue-600 to-cyan-700 border-0 shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
            <CardContent className="p-12 lg:p-16 relative text-white text-center">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Machen Sie Ihre IT sicherer
              </h2>
              <p className="text-xl text-cyan-100 mb-10 max-w-2xl mx-auto">
                Lassen Sie uns schauen, welche Sicherheitsmaßnahmen für Sie am wichtigsten sind.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Button size="lg" asChild className="bg-white text-cyan-700 hover:bg-cyan-50 shadow-xl px-8 py-4 text-lg">
                  <Link to="/contact">
                    <Target className="mr-2 h-5 w-5" />
                    Jetzt Gespräch vereinbaren
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-4 text-lg">
                  <a href="tel:015679697252">
                    <Phone className="mr-2 h-5 w-5" />
                    0156-79697252
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default ITSicherheitISMS;